import type { FolderItem } from './WorkspacePicker'

export type MicrosoftFilesProviderType = 'onedrive' | 'sharepoint'

export type BrowseNodeKind = 'root' | 'site' | 'library' | 'folder' | 'file'

export interface BrowseNode {
  id: string
  name: string
  path: string
  kind: BrowseNodeKind
  has_children: boolean
  is_file?: boolean
  drive_id?: string
  is_drive_root?: boolean
  drive_name?: string
  /** Graph drive-item id used when creating a sync (library root id). */
  item_id?: string
  site_id?: string
  web_url?: string
  children?: BrowseNode[]
  next_link?: string | null
}

export const ONEDRIVE_ROOT_ID = 'onedrive-root'

export const ONEDRIVE_ROOT: BrowseNode = {
  id: ONEDRIVE_ROOT_ID,
  name: 'My files',
  path: '',
  kind: 'root',
  has_children: true
}

export function siteNodeId(siteId: string): string {
  return `site:${siteId}`
}

export function libraryNodeId(driveId: string): string {
  return `lib:${driveId}`
}

export function isDefaultDocumentsLibrary(node: BrowseNode): boolean {
  return node.kind === 'library' && node.name.trim().toLowerCase() === 'documents'
}

export function isSelectableSyncFolder(node: BrowseNode): boolean {
  if (node.is_file || node.kind === 'file' || node.kind === 'root' || node.kind === 'site') {
    return false
  }
  if (node.kind === 'library') {
    return !isDefaultDocumentsLibrary(node) && Boolean(node.item_id)
  }
  return node.kind === 'folder'
}

export function isBrowseOnlyLocation(node: BrowseNode): boolean {
  return node.kind === 'root' || node.kind === 'site' || isDefaultDocumentsLibrary(node)
}

export function nodeCanExpand(node: BrowseNode): boolean {
  if (node.children !== undefined) {
    return node.children.length > 0 || Boolean(node.next_link)
  }
  return Boolean(node.has_children)
}

export function withLoadedChildren(
  node: BrowseNode,
  children: BrowseNode[],
  nextLink: string | null
): BrowseNode {
  return {
    ...node,
    children,
    has_children: children.length > 0 || Boolean(nextLink),
    next_link: nextLink
  }
}

export function browseOnlyHint(
  node: BrowseNode | undefined,
  providerType: MicrosoftFilesProviderType
): string | null {
  if (node && isSelectableSyncFolder(node)) {
    return null
  }
  if (!node) {
    return providerType === 'sharepoint'
      ? 'Open a site, then a library or folder, then click Next.'
      : 'Open a folder, then click Next to sync this folder.'
  }
  if (isDefaultDocumentsLibrary(node) || (node.kind === 'library' && !node.item_id)) {
    return `"${node.name}" is a library and cannot be synced. Open a folder inside it, then click Next.`
  }
  if (node.kind === 'site') {
    return `"${node.name}" is a site and cannot be synced. Open a library or folder, then click Next.`
  }
  if (node.kind === 'file' || node.is_file) {
    return 'Files cannot be selected for sync.'
  }
  return 'This location cannot be synced. Open a folder inside it, then click Next.'
}

export function toFolderItem(node: BrowseNode): FolderItem {
  return {
    id: node.item_id || node.id,
    name: node.name,
    path: node.path,
    has_children: node.has_children,
    is_file: node.is_file,
    drive_id: node.drive_id,
    is_drive_root: node.is_drive_root,
    drive_name: node.drive_name
  }
}

export function siteToNode(site: { site_id: string; name: string; web_url?: string }): BrowseNode {
  return {
    id: siteNodeId(site.site_id),
    name: site.name,
    path: `/${site.name}`,
    kind: 'site',
    has_children: true,
    site_id: site.site_id,
    web_url: site.web_url
  }
}

export function driveToNode(drive: {
  drive_id: string
  name: string
  root_id?: string
  web_url?: string
  has_children?: boolean
}): BrowseNode {
  return {
    id: libraryNodeId(drive.drive_id),
    name: drive.name,
    path: `/${drive.name}`,
    kind: 'library',
    // Fail open: only hide the chevron when the API explicitly says no
    // subfolders. A missing field (older backend) keeps the chevron.
    has_children: drive.has_children !== false,
    drive_id: drive.drive_id,
    is_drive_root: true,
    drive_name: drive.name,
    web_url: drive.web_url,
    item_id: drive.root_id
  }
}

export function folderRowToNode(row: FolderItem): BrowseNode {
  return {
    id: row.id,
    name: row.name,
    path: row.path,
    kind: row.is_file ? 'file' : 'folder',
    has_children: Boolean(row.has_children && !row.is_file),
    is_file: row.is_file,
    drive_id: row.drive_id,
    is_drive_root: row.is_drive_root,
    drive_name: row.drive_name
  }
}

export function folderItemsFromResponse(data: { folders?: FolderItem[]; files?: FolderItem[] }): BrowseNode[] {
  const folders = (data.folders || []).map(folderRowToNode)
  const files = (data.files || []).map(folderRowToNode)
  return [...folders, ...files]
}

export function treeChildrenFromContents(items: BrowseNode[]): BrowseNode[] {
  return items.filter((item) => item.kind !== 'file' && !item.is_file)
}

export function mapTree(
  nodes: BrowseNode[],
  targetId: string,
  updater: (node: BrowseNode) => BrowseNode
): BrowseNode[] {
  return nodes.map((node) => {
    if (node.id === targetId) {
      return updater(node)
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: mapTree(node.children, targetId, updater)
      }
    }
    return node
  })
}

export function findNode(nodes: BrowseNode[], targetId: string): BrowseNode | undefined {
  for (const node of nodes) {
    if (node.id === targetId) {
      return node
    }
    if (node.children && node.children.length > 0) {
      const found = findNode(node.children, targetId)
      if (found) {
        return found
      }
    }
  }
  return undefined
}

export function removeNodeById(nodes: BrowseNode[], targetId: string): BrowseNode[] {
  return nodes
    .filter((node) => node.id !== targetId)
    .map((node) =>
      node.children
        ? { ...node, children: removeNodeById(node.children, targetId) }
        : node
    )
}

export function graphErrorStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } }).response?.status
}

export function isMicrosoftResourceUnavailable(err: unknown): boolean {
  const status = graphErrorStatus(err)
  return status === 403 || status === 404
}

export async function mapPool<T, R>(
  items: T[],
  limit: number,
  mapper: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length)
  let nextIndex = 0

  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex
      nextIndex += 1
      results[index] = await mapper(items[index])
    }
  }

  const workerCount = Math.max(1, Math.min(limit, items.length))
  await Promise.all(Array.from({ length: workerCount }, () => worker()))
  return results
}

export function findPathToNode(
  nodes: BrowseNode[],
  targetId: string,
  trail: BrowseNode[] = []
): BrowseNode[] | null {
  for (const node of nodes) {
    const next = [...trail, node]
    if (node.id === targetId) {
      return next
    }
    if (node.children && node.children.length > 0) {
      const found = findPathToNode(node.children, targetId, next)
      if (found) {
        return found
      }
    }
  }
  return null
}

export function graphErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
  if (typeof detail === 'string') {
    return detail
  }
  if (detail && typeof detail === 'object' && 'message' in detail) {
    const message = (detail as { message?: unknown }).message
    if (typeof message === 'string') {
      return message
    }
  }
  if (err instanceof Error && err.message) {
    return err.message
  }
  return fallback
}
